import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Video, Upload, Loader2, CheckCircle, XCircle, Play, 
  Trash2, RefreshCw, Music, Image, FileImage, AlertCircle
} from 'lucide-react';

const VirtualTourGenerator = ({ propertyId, propertyTitle, existingImages = [], existingFloorplans = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [tour, setTour] = useState(null);
  const [musicOptions, setMusicOptions] = useState([]);
  const [selectedMusic, setSelectedMusic] = useState('elegant');
  
  // File selection
  const [floorPlanUrl, setFloorPlanUrl] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  
  // Polling interval ref
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchMusicOptions();
      fetchExistingTour();
      
      // Pre-fill with existing images if available
      if (existingFloorplans.length > 0) {
        setFloorPlanUrl(existingFloorplans[0]);
      }
      if (existingImages.length > 0) {
        setSelectedPhotos(existingImages.slice(0, 10));
      }
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, propertyId, existingImages, existingFloorplans]);

  const fetchMusicOptions = async () => {
    try {
      const response = await axios.get('/virtual-tour/music-options');
      setMusicOptions(response.data);
    } catch (error) {
      console.error('Error fetching music options:', error);
    }
  };

  const fetchExistingTour = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/property/${propertyId}/virtual-tour`);
      if (response.data.tour) {
        setTour(response.data.tour);
        
        // If tour is in progress, start polling
        if (['pending', 'analyzing', 'generating'].includes(response.data.tour.status)) {
          startPolling(response.data.tour.tour_id);
        }
      }
    } catch (error) {
      console.error('Error fetching tour:', error);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (tourId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`/agent/virtual-tour/${tourId}`);
        setTour(response.data);
        
        if (['completed', 'failed'].includes(response.data.status)) {
          clearInterval(pollIntervalRef.current);
          setGenerating(false);
          
          if (response.data.status === 'completed') {
            toast.success('Virtual Tour completato!');
          } else if (response.data.status === 'failed') {
            toast.error(`Errore: ${response.data.error_message || 'Generazione fallita'}`);
          }
        }
      } catch (error) {
        console.error('Error polling tour status:', error);
      }
    }, 3000); // Poll every 3 seconds
  };

  const handleGenerate = async () => {
    if (!floorPlanUrl) {
      toast.error('Seleziona una planimetria');
      return;
    }
    
    if (selectedPhotos.length < 1) {
      toast.error('Seleziona almeno una foto');
      return;
    }
    
    setGenerating(true);
    
    try {
      const response = await axios.post('/agent/virtual-tour/generate', {
        property_id: propertyId,
        floor_plan_url: floorPlanUrl,
        photo_urls: selectedPhotos,
        music_id: selectedMusic
      });
      
      setTour({
        tour_id: response.data.tour_id,
        status: response.data.status,
        progress: 0,
        progress_message: 'In coda per elaborazione...'
      });
      
      toast.success('Generazione Virtual Tour avviata');
      startPolling(response.data.tour_id);
      
    } catch (error) {
      setGenerating(false);
      const message = error.response?.data?.detail || 'Errore nella generazione';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!tour?.tour_id) return;
    
    if (!confirm('Sei sicuro di voler eliminare questo virtual tour?')) {
      return;
    }
    
    try {
      await axios.delete(`/agent/virtual-tour/${tour.tour_id}`);
      setTour(null);
      toast.success('Virtual Tour eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const togglePhotoSelection = (url) => {
    setSelectedPhotos(prev => {
      if (prev.includes(url)) {
        return prev.filter(p => p !== url);
      } else if (prev.length < 10) {
        return [...prev, url];
      } else {
        toast.error('Massimo 10 foto selezionabili');
        return prev;
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'failed': return 'bg-red-100 text-red-700 border-red-200';
      case 'pending':
      case 'analyzing':
      case 'generating': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'In coda';
      case 'analyzing': return 'Analisi AI';
      case 'generating': return 'Generazione video';
      case 'completed': return 'Completato';
      case 'failed': return 'Fallito';
      default: return status;
    }
  };

  const isInProgress = tour && ['pending', 'analyzing', 'generating'].includes(tour.status);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="text-purple-600 border-purple-200 hover:bg-purple-50"
          data-testid="create-virtual-tour-btn"
        >
          <Video className="h-4 w-4 mr-1" />
          Virtual Tour AI
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Video className="h-5 w-5 text-purple-600" />
            <span>Genera Virtual Tour AI</span>
          </DialogTitle>
          <DialogDescription>
            {propertyTitle}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : tour?.status === 'completed' ? (
          // Completed tour view
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Badge className={getStatusColor(tour.status)}>
                <CheckCircle className="h-3 w-3 mr-1" />
                {getStatusLabel(tour.status)}
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDelete}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Elimina
              </Button>
            </div>
            
            {/* Video Player */}
            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
              {tour.video_url ? (
                <video 
                  src={tour.video_url} 
                  controls 
                  className="w-full h-full"
                  poster={tour.thumbnail_url}
                  data-testid="virtual-tour-video"
                >
                  Il tuo browser non supporta i video HTML5.
                </video>
              ) : (
                <div className="flex items-center justify-center h-full text-white/60">
                  <div className="text-center">
                    <Play className="h-12 w-12 mx-auto mb-2" />
                    <p>Video disponibile</p>
                    <p className="text-sm text-white/40">Durata: {tour.duration_seconds}s</p>
                  </div>
                </div>
              )}
            </div>

            {/* Analysis Results */}
            {tour.analysis_result && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-medium text-purple-800 mb-2">Analisi AI</h4>
                <div className="text-sm text-purple-700">
                  <p><strong>Stanze identificate:</strong> {tour.analysis_result.layout?.rooms?.join(', ')}</p>
                  <p><strong>Percorso di visita:</strong> {tour.analysis_result.visit_sequence?.join(' → ')}</p>
                  <p><strong>Foto analizzate:</strong> {tour.analysis_result.total_photos}</p>
                </div>
              </div>
            )}

            {/* Generate new button */}
            <Button 
              onClick={() => setTour(null)} 
              variant="outline" 
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Genera Nuovo Virtual Tour
            </Button>
          </div>
        ) : isInProgress ? (
          // In progress view
          <div className="space-y-6 py-4">
            <div className="text-center">
              <div className="relative inline-flex">
                <Loader2 className="h-16 w-16 animate-spin text-purple-600" />
                <Video className="h-6 w-6 text-purple-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <Badge className={getStatusColor(tour.status)}>
                  {getStatusLabel(tour.status)}
                </Badge>
                <span className="text-gray-500">{tour.progress || 0}%</span>
              </div>
              <Progress value={tour.progress || 0} className="h-2" />
              <p className="text-sm text-center text-gray-600">
                {tour.progress_message || 'Elaborazione in corso...'}
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Generazione in corso</p>
                  <p className="mt-1">
                    L'AI sta analizzando la planimetria e le foto per creare un video tour cinematografico. 
                    Questa operazione potrebbe richiedere alcuni minuti.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : tour?.status === 'failed' ? (
          // Failed view
          <div className="space-y-4 py-4">
            <div className="text-center">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Generazione Fallita</h3>
              <p className="text-red-600 mt-2">{tour.error_message || 'Si è verificato un errore'}</p>
            </div>
            
            <Button 
              onClick={() => setTour(null)} 
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Riprova
            </Button>
          </div>
        ) : (
          // New generation form
          <div className="space-y-6">
            {/* Floor Plan Selection */}
            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <FileImage className="h-4 w-4 text-gray-500" />
                <span>Planimetria *</span>
              </Label>
              
              {existingFloorplans.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {existingFloorplans.map((url, idx) => (
                    <div 
                      key={idx}
                      onClick={() => setFloorPlanUrl(url)}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        floorPlanUrl === url 
                          ? 'border-purple-500 ring-2 ring-purple-200' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={url} alt={`Planimetria ${idx + 1}`} className="w-full h-full object-cover" />
                      {floorPlanUrl === url && (
                        <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                          <CheckCircle className="h-6 w-6 text-purple-600" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileImage className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nessuna planimetria disponibile</p>
                  <p className="text-xs text-gray-400 mt-1">Carica una planimetria nella sezione multimedia</p>
                </div>
              )}
            </div>

            {/* Photo Selection */}
            <div className="space-y-2">
              <Label className="flex items-center justify-between">
                <span className="flex items-center space-x-2">
                  <Image className="h-4 w-4 text-gray-500" />
                  <span>Foto da includere * ({selectedPhotos.length}/10)</span>
                </span>
              </Label>
              
              {existingImages.length > 0 ? (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-1">
                  {existingImages.map((url, idx) => (
                    <div 
                      key={idx}
                      onClick={() => togglePhotoSelection(url)}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        selectedPhotos.includes(url) 
                          ? 'border-purple-500 ring-2 ring-purple-200' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      {selectedPhotos.includes(url) && (
                        <div className="absolute top-1 right-1 bg-purple-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          {selectedPhotos.indexOf(url) + 1}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Nessuna foto disponibile</p>
                  <p className="text-xs text-gray-400 mt-1">Carica foto nella sezione multimedia</p>
                </div>
              )}
            </div>

            {/* Music Selection */}
            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <Music className="h-4 w-4 text-gray-500" />
                <span>Musica di sottofondo</span>
              </Label>
              <Select value={selectedMusic} onValueChange={setSelectedMusic}>
                <SelectTrigger data-testid="music-select">
                  <SelectValue placeholder="Seleziona musica" />
                </SelectTrigger>
                <SelectContent>
                  {musicOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Info Box */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Video className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-700">
                  <p className="font-medium">Come funziona?</p>
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    <li>L'AI analizza la planimetria per identificare le stanze</li>
                    <li>Le foto vengono associate alle stanze corrispondenti</li>
                    <li>Viene generato un video tour cinematografico con Sora 2</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerate}
              disabled={generating || !floorPlanUrl || selectedPhotos.length < 1}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="generate-tour-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Avvio generazione...
                </>
              ) : (
                <>
                  <Video className="h-4 w-4 mr-2" />
                  Genera Virtual Tour
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VirtualTourGenerator;
